import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  CardHeader,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../../store/dashboardUser/dashboardUserSlice';
import Loader from '../../../components/Loader';
import RefreshIcon from '@mui/icons-material/Refresh';
import { findDashboardUser } from '../../../store/dashboardUser/api';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import { Users } from '../../../store/users/api/interfaces';
import useToggle from '../../../hooks/useToggle';
import UsersActionModal from '../../Users/UserActionModal';
import { styled } from '@mui/material/styles';

// Add this styled component for the image viewer modal
const StyledImageViewerModal = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: theme.zIndex.modal + 100, // Ensure it's above other modals
}));

const DashboardUserTable = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.dashboardUser);
  const [actionUser, setActionUser] = useState<Users>();
  const {
    toggle: toggleAction,
    onOpen: onOpenAction,
    onClose: onCloseAction,
  } = useToggle();

  // Add state for image viewer
  const [viewImage, setViewImage] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  
  // Add state for user role filter (company or broker)
  const [selectedRole, setSelectedRole] = useState<'company' | 'broker'>('company');

  useEffect(() => {
    dispatch(
      findDashboardUser({
        limit,
        skip,
        isApproved: false,
        userRole: selectedRole,
      })
    );
  }, [dispatch, limit, skip, selectedRole]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleRefresh = () => {
    dispatch(
      findDashboardUser({
        limit,
        skip,
        isApproved: false,
        userRole: selectedRole,
      })
    );
  };

  const handleRoleChange = (event: React.SyntheticEvent, newValue: 'company' | 'broker') => {
    setSelectedRole(newValue);
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const handleActionUpdate = (value: Users): void => {
    setActionUser(value);
    onOpenAction();
  };

  // Function to close image viewer
  const handleCloseImageViewer = () => {
    setViewImage(false);
    setCurrentImage('');
  };

  return (
    <Card>
      <CardHeader
        title={selectedRole === 'company' ? 'Not Approved Company Users' : 'Not Approved Broker Users'}
        action={
          <IconButton
            aria-label='settings'
            onClick={() => handleRefresh()}
          >
            <RefreshIcon />
          </IconButton>
        }
        sx={{ mb: 2 }}
      />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedRole} onChange={handleRoleChange} aria-label="user role tabs">
          <Tab label="Companies" value="company" />
          <Tab label="Brokers" value="broker" />
        </Tabs>
      </Box>

        <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
          <Table size={'medium'} sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Name'}</TableCell>
                <TableCell align='center'>{'Email'}</TableCell>
                <TableCell align='center'>{'Mobile Number'}</TableCell>
                <TableCell align='center'>{'Role'}</TableCell>
                <TableCell align='center'>{'Status'}</TableCell>
                <TableCell align='center'>{'Approved'}</TableCell>
                <TableCell align='center'>{'Reason'}</TableCell>
                <TableCell align='center'>{'Action'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={7}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell align='center'>{user?.name}</TableCell>
                    <TableCell align='center'>{user?.email}</TableCell>
                    <TableCell align='center'>
                      {user?.countryCallingCode}-{user?.mobileNumber}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {user?.userRole}
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={user.activeState ? 'success' : 'error'}
                      >
                        {user.activeState ? 'Active' : 'InActive'}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={user.isApproved ? 'success' : 'error'}
                      >
                        {user.isApproved ? 'Approved' : 'Not Approved'}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      {user.rejectionReason ? (
                        <Tooltip
                          title={user.rejectionReason}
                          arrow
                        >
                          <span style={{ cursor: 'pointer' }}>
                            {user.rejectionReason.length > 10
                              ? `${user.rejectionReason.substring(0, 10)}...`
                              : user.rejectionReason}
                          </span>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align='center'>
                      {(user?.userRole === 'company' || user?.userRole === 'broker') &&
                      !user?.isApproved &&
                      user?.activeState ? (
                        <IconButton
                          aria-label='edit'
                          onClick={() => handleActionUpdate(user)}
                        >
                          <Iconify icon={'eva:edit-outline'} />
                        </IconButton>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>

      {toggleAction && (
        <UsersActionModal
          toggle={toggleAction}
          onClose={onCloseAction}
          actionUser={actionUser}
          onRefresh={handleRefresh}
        />
      )}

      {viewImage && (
        <StyledImageViewerModal onClick={handleCloseImageViewer}>
          <img 
            src={currentImage} 
            alt="Expanded view" 
            style={{ 
              maxWidth: '90%', 
              maxHeight: '90%', 
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image
          />
        </StyledImageViewerModal>
      )}
    </Card>
  );
};

export default DashboardUserTable;
